// src/utils/apiClient.js
import { BASE_URL } from './config';
import { isDebugMode } from './debugUtils'; // Import debug utility

let _updateToken = null;
let _updateUser = null;
let _navigate = null;

export const initApiClient = ({ updateToken, updateUser, navigate }) => {
  _updateToken = updateToken;
  _updateUser = updateUser;
  _navigate = navigate;
};

const _handleLogout = () => {
  if (isDebugMode()) {
    console.log('[apiClient] Session invalid. Logging out user.');
  }

  if (_updateUser) {
    _updateUser({
      fullName: '',
      username: '',
      email: '',
      isLoggedIn: false,
      role: 'Guest',
      token: null,
      itemsPerPage: 12,
      subscribe: false,
      creditPoints: 0,
    });
  }
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user'); // Ensure user context persistence is also cleared
  if (_navigate) {
    _navigate('/');
  } else {
    // Fallback if navigate is not initialized, though it should be.
    window.location.href = '/';
  }
};

export const apiFetch = async (url, options = {}, currentToken) => {
  if (!_updateToken || !_updateUser || !_navigate) {
    console.error('ApiClient not initialized. Call initApiClient first.');
    // Potentially redirect to an error page or login if critical
    throw new Error('ApiClient not initialized.');
  }

  // --- Prerender Gating (Step 1 from plan) ---
  if (navigator.userAgent.includes('ReactSnap')) {
    //    console.log(`[Prerender] Intercepted API call: ${url}`);
    // Return canned successful responses for startup calls to prevent network errors.
    if (url.endsWith('/api/announcements/active') || url.endsWith('/api/config') || url.endsWith('/api/sessions/ping') || url.endsWith('/api/config/shutdown-status')) {
      return new Response(JSON.stringify({ ok: true, prerender: true }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }
    // Block other API calls during prerender to be safe
    //   console.warn(`[Prerender] Blocking API call to avoid side-effects: ${url}`);
    return new Response(JSON.stringify({ message: 'Blocked during prerender' }), {
      status: 403, // Forbidden
      headers: { 'Content-Type': 'application/json' },
    });
  }
  // --- End Prerender Gating ---
  const headers = {
    ...options.headers,
  };

  if (currentToken) {
    headers.Authorization = `Bearer ${currentToken}`;
  }

  const fetchOptions = {
    ...options,
    headers,
    credentials: 'include', // Always send cookies with API requests.
  };

  let response = await fetch(url, fetchOptions);

  // --- HTML Response Guard ---
  // After a server restart, a valid token might be sent, but the server session is gone.
  // Some servers redirect to index.html (200 OK, text/html) instead of sending a 401.
  // This block catches that case and forces a logout to prevent JSON parsing errors.
  const contentType = response.headers.get('content-type');
  if (isDebugMode() && contentType && contentType.includes('text/html')) {
    // --- WHITELIST FOR INITIAL API CALLS ---
    // After login, a race condition can occur where the frontend makes API calls before the backend session is fully ready.
    // This can cause the server to return HTML instead of JSON. We whitelist these specific initial calls to prevent an
    // immediate logout loop. If they fail, they will be retried by their respective components' logic.
    const whitelistedUrls = [
      '/api/sessions/count', // From SessionTracker
      '/api/config', // From ConfigContext
      '/api/announcements/active', // From GlobalAnnouncement
      '/api/cart', // From CartContext
    ];

    if (whitelistedUrls.some((whitelistedUrl) => url.includes(whitelistedUrl))) {
      console.warn(`[apiClient] Received HTML from a whitelisted URL (${url}). Ignoring to prevent login loop. The component will likely retry.`);
    } else {
      console.warn(`[apiClient] Expected JSON but received HTML from ${url}. This likely indicates a session expiry. Forcing logout.`);
      _handleLogout();
      // Throw a controlled error to stop the promise chain and prevent parsing HTML as JSON.
      throw new Error('Received HTML instead of JSON, indicating a session timeout.');
    }
  }

  if (response.status === 401) {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      _handleLogout();
      throw new Error('Session expired. No refresh token. Please log in again.');
    }

    try {
      const refreshResponse = await fetch(`${BASE_URL}/authUser/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include', // Also include credentials on the refresh request.
      });

      if (!refreshResponse.ok) {
        _handleLogout();
        throw new Error('Failed to refresh token. Please log in again.');
      }

      const { accessToken: newAccessToken } = await refreshResponse.json();
      _updateToken(newAccessToken); // Update context and localStorage

      fetchOptions.headers.Authorization = `Bearer ${newAccessToken}`; // Update header for retry
      response = await fetch(url, fetchOptions); // Retry original request
    } catch (refreshError) {
      _handleLogout();
      throw new Error(refreshError.message || 'Session expired during refresh. Please log in again.');
    }
  }
  return response;
};
